import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ProjectModal from '../../components/ProjectModal';
import ProjectCard from '../../components/ProjectCard';
import { useLanguage } from '../../i18n';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import { PROJECTS } from '../../data/projects';
import './Proyectos.scss';

gsap.registerPlugin(ScrollTrigger);

const CLOSE_ANIMATION_MS = 300;

const Proyectos = () => {
  const { t } = useLanguage();
  const reduceMotion = usePrefersReducedMotion();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const projects = useMemo(
    () =>
      PROJECTS.map((project) => ({
        ...project,
        description: t(`proyectos.${project.id}.description`),
      })),
    [t],
  );

  useEffect(() => {
    if (reduceMotion) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: titleRef.current, start: 'top 85%' },
        },
      );

      gsap.fromTo(
        '.proyectos__accent',
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1,
          ease: 'power2.inOut',
          scrollTrigger: { trigger: '.proyectos__accent', start: 'top 85%' },
        },
      );

      gsap.fromTo(
        '.proyectos__card',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: { trigger: '.proyectos__grid', start: 'top 80%' },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [reduceMotion]);

  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  const handleProjectClick = useCallback((project) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setSelectedProject(project);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    closeTimeoutRef.current = setTimeout(() => {
      setSelectedProject(null);
      closeTimeoutRef.current = null;
    }, CLOSE_ANIMATION_MS);
  }, []);

  return (
    <section
      className="proyectos"
      id="proyectos"
      ref={sectionRef}
      aria-labelledby="proyectos-title"
    >
      <div className="container">
        <header className="proyectos__header">
          <span className="proyectos__eyebrow">01 — Portfolio</span>
          <h2 id="proyectos-title" className="proyectos__title" ref={titleRef}>
            {t('proyectos.title')}
          </h2>
          <span className="proyectos__accent" aria-hidden="true" />
        </header>

        <div className="proyectos__grid">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={handleProjectClick}
              label={`${t('proyectos.openProject')}: ${project.name}`}
              viewLabel={t('proyectos.openProject')}
            />
          ))}
        </div>
      </div>

      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  );
};

export default Proyectos;
